import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  SlidersHorizontal, X, MagnifyingGlass, ArrowRight, BellRinging,
  SquaresFour, MapTrifold, CaretLeft, CaretRight, ChartLineUp,
  ShareNetwork, CaretDown, Columns,
} from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import SiteNav from "@/components/SiteNav";
import PropertyCard from "@/components/PropertyCard";
import ListingsMap from "@/components/ListingsMap";
import {
  REGIONS, LISTING_TYPES, AREA_GROUPS, LIFESTYLE_FILTERS,
  MIN_BED_CHIPS, MAX_BED_CHIPS, FLOOD_RISK_FILTERS, SCHOOL_DISTRICTS, formatPrice,
  propertyTypesForListingType,
} from "@/lib/constants";

const SALE_PRICE_OPTIONS = [
  { label: "Any price", min: "", max: "" },
  { label: "Under $400k", min: "", max: "400000" },
  { label: "$400k – $700k", min: "400000", max: "700000" },
  { label: "$700k – $1M", min: "700000", max: "1000000" },
  { label: "$1M+", min: "1000000", max: "" },
];

const RENT_PRICE_OPTIONS = [
  { label: "Any rent", min: "", max: "" },
  { label: "Under $2,500", min: "", max: "2500" },
  { label: "Under $3,000", min: "", max: "3000" },
  { label: "$2,500 – $4,000", min: "2500", max: "4000" },
  { label: "$4,000 – $6,000", min: "4000", max: "6000" },
  { label: "$6,000+", min: "6000", max: "" },
];

const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "price_asc", label: "Price ↑" },
  { value: "price_desc", label: "Price ↓" },
  { value: "beds_desc", label: "Most beds" },
];

const PAGE_SIZE = 12;
const INITIAL_FETCH_LIMIT = 48;
const FETCH_STEP = 48;

function Chip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-3.5 py-2 rounded-sm text-xs uppercase tracking-wider border transition-colors ${
        active ? "border-brand text-brand bg-brand/10" : "border-white/10 text-white/50 hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

function FilterSection({ label, hint, children }) {
  return (
    <div>
      <div className="mb-2.5">
        <p className="text-xs uppercase tracking-[0.16em] text-white/70 font-medium">{label}</p>
        {hint ? <p className="text-[11px] text-white/35 mt-1 leading-snug">{hint}</p> : null}
      </div>
      {children}
    </div>
  );
}

function slug(s) {
  return String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

export default function Listings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [feedMeta, setFeedMeta] = useState(null);
  const [recentSales, setRecentSales] = useState([]);
  const [page, setPage] = useState(1);
  const [fetchLimit, setFetchLimit] = useState(INITIAL_FETCH_LIMIT);
  const [filtersOpen, setFiltersOpen] = useState(true);

  const initialRegions = (searchParams.get("region") || "").split(",").filter(Boolean);
  const initialTypes = (searchParams.get("property_type") || "").split(",").filter(Boolean);
  const initialGroups = (searchParams.get("area_group") || "").split(",").filter(Boolean);
  const initialAmenities = (searchParams.get("amenities") || "").split(",").filter(Boolean);

  const [regions, setRegions] = useState(initialRegions);
  const [areaGroups, setAreaGroups] = useState(initialGroups);
  const [types, setTypes] = useState(initialTypes);
  const [priceIdx, setPriceIdx] = useState(Number(searchParams.get("price_idx") || 0) || 0);
  const [minBeds, setMinBeds] = useState(searchParams.get("min_bedrooms") || "");
  const [maxBeds, setMaxBeds] = useState(searchParams.get("max_bedrooms") || "");
  const [minBaths, setMinBaths] = useState(searchParams.get("min_bathrooms") || "");
  const [view, setView] = useState(searchParams.get("view") || "grid");
  const [sort, setSort] = useState(searchParams.get("sort") || "newest");
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [qDraft, setQDraft] = useState(searchParams.get("q") || "");
  const [source, setSource] = useState(searchParams.get("source") || "all");
  // Only sale / rent — "all" was a fence-sitter tab that made price / home-type filters noisy.
  // Legacy URL params (`listing_type=all` or missing) default to sale.
  const initialListingType = (() => {
    const raw = (searchParams.get("listing_type") || "").toLowerCase();
    return raw === "rent" ? "rent" : "sale";
  })();
  const [listingType, setListingType] = useState(initialListingType);
  const [nearTransit, setNearTransit] = useState(searchParams.get("near_transit") === "1");
  const [walkIn, setWalkIn] = useState(searchParams.get("walk_in") === "1");
  const [amenities, setAmenities] = useState(initialAmenities);
  const [floodRisk, setFloodRisk] = useState(searchParams.get("flood_risk") || "any");
  const [schoolDistrict, setSchoolDistrict] = useState(searchParams.get("school_district") || "");

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertForm, setAlertForm] = useState({ name: "", email: "", phone: "" });
  const [savingAlert, setSavingAlert] = useState(false);

  const priceOptions = listingType === "rent" ? RENT_PRICE_OPTIONS : SALE_PRICE_OPTIONS;
  const homeTypes = propertyTypesForListingType(listingType);

  // Collapse dense filters on small screens by default after first paint.
  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth < 768) {
      setFiltersOpen(false);
    }
  }, []);

  const saveAlert = async () => {
    if (!alertForm.name || !alertForm.email) {
      toast.error("Please enter your name and email");
      return;
    }
    setSavingAlert(true);
    const opts = listingType === "rent" ? RENT_PRICE_OPTIONS : SALE_PRICE_OPTIONS;
    const price = opts[priceIdx] || opts[0];
    try {
      await api.post("/saved-searches", {
        name: alertForm.name,
        email: alertForm.email,
        phone: alertForm.phone || null,
        regions,
        property_types: types,
        min_price: price.min ? Number(price.min) : null,
        max_price: price.max ? Number(price.max) : null,
        min_bedrooms: minBeds ? Number(minBeds) : null,
        listing_type: listingType,
      });
      toast.success("Alert saved! You'll be the first to know about new matches.");
      setAlertOpen(false);
      setAlertForm({ name: "", email: "", phone: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSavingAlert(false);
    }
  };

  // Reset fetch window when filters change (not when only loading more).
  useEffect(() => {
    setFetchLimit(INITIAL_FETCH_LIMIT);
  }, [regions, areaGroups, types, priceIdx, minBeds, maxBeds, minBaths, sort, q, source, listingType, nearTransit, walkIn, amenities, floodRisk, schoolDistrict]);

  // Drop rent-only amenity chips when switching to For Sale.
  useEffect(() => {
    if (listingType !== "rent") {
      setAmenities((prev) => prev.filter((a) => a !== "no_fee"));
    }
  }, [listingType]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    // Keep list/map snappy with 1.5k+ inventory — raise via "Show more matches".
    const params = { sort, limit: fetchLimit };
    if (regions.length) params.region = regions.join(",");
    if (areaGroups.length) params.area_group = areaGroups.join(",");
    if (types.length) params.property_type = types.join(",");
    const opts = listingType === "rent" ? RENT_PRICE_OPTIONS : SALE_PRICE_OPTIONS;
    const price = opts[priceIdx] || opts[0];
    if (price?.min) params.min_price = price.min;
    if (price?.max) params.max_price = price.max;
    if (minBeds) params.min_bedrooms = minBeds;
    if (maxBeds) params.max_bedrooms = maxBeds;
    if (minBaths) params.min_bathrooms = minBaths;
    if (q) params.q = q;
    if (source && source !== "all") params.source = source;
    params.listing_type = listingType; // always sale or rent now
    if (nearTransit) params.near_transit = true;
    if (walkIn) params.walk_in = true;
    if (amenities.length) params.amenities = amenities.join(",");
    if (amenities.includes("no_fee")) params.no_fee = true;
    if (amenities.includes("pets")) params.pets = true;
    if (floodRisk && floodRisk !== "any") params.flood_risk = floodRisk;
    if (schoolDistrict) params.school_district = schoolDistrict;
    try {
      const { data } = await api.get("/properties", { params });
      setProperties(Array.isArray(data) ? data : []);
      if (fetchLimit === INITIAL_FETCH_LIMIT) setPage(1);
    } catch (e) {
      setProperties([]);
      setLoadError(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Could not load listings");
    } finally {
      setLoading(false);
    }
  }, [regions, areaGroups, types, priceIdx, minBeds, maxBeds, minBaths, sort, q, source, listingType, nearTransit, walkIn, amenities, floodRisk, schoolDistrict, fetchLimit]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    load();
    const sp = {};
    if (regions.length) sp.region = regions.join(",");
    if (areaGroups.length) sp.area_group = areaGroups.join(",");
    if (types.length) sp.property_type = types.join(",");
    if (q) sp.q = q;
    if (sort && sort !== "newest") sp.sort = sort;
    if (source && source !== "all") sp.source = source;
    if (listingType === "rent") sp.listing_type = "rent";
    if (minBeds) sp.min_bedrooms = minBeds;
    if (maxBeds) sp.max_bedrooms = maxBeds;
    if (minBaths) sp.min_bathrooms = minBaths;
    if (nearTransit) sp.near_transit = "1";
    if (walkIn) sp.walk_in = "1";
    if (amenities.length) sp.amenities = amenities.join(",");
    if (floodRisk && floodRisk !== "any") sp.flood_risk = floodRisk;
    if (schoolDistrict) sp.school_district = schoolDistrict;
    if (priceIdx) sp.price_idx = String(priceIdx);
    if (view && view !== "grid") sp.view = view;
    setSearchParams(sp, { replace: true });
  }, [load, view]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("/feeds/status").then(({ data }) => setFeedMeta(data)).catch(() => {});
    api.get("/market/recent-sales", { params: { limit: 8 } })
      .then(({ data }) => setRecentSales(data?.sales || []))
      .catch(() => {});
  }, []);

  const toggle = (list, setList, val) =>
    setList(list.includes(val) ? list.filter((x) => x !== val) : [...list, val]);

  const clearAll = () => {
    setRegions([]);
    setAreaGroups([]);
    setTypes([]);
    setPriceIdx(0);
    setMinBeds("");
    setMaxBeds("");
    setMinBaths("");
    setQ("");
    setQDraft("");
    setSource("all");
    setListingType("sale");
    setNearTransit(false);
    setWalkIn(false);
    setAmenities([]);
    setFloodRisk("any");
    setSchoolDistrict("");
    setSort("newest");
  };

  const hasFilters = regions.length || areaGroups.length || types.length || priceIdx || minBeds || maxBeds
    || minBaths || q || (source && source !== "all") || listingType === "rent"
    || nearTransit || walkIn || amenities.length || (floodRisk && floodRisk !== "any") || schoolDistrict;

  const shareSearch = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Homes from The Whyte Glove", url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Search link copied — send it to anyone.");
      }
    } catch {
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Search link copied — send it to anyone.");
      } catch {
        toast.error("Could not copy link");
      }
    }
  };

  const totalPages = Math.max(1, Math.ceil(properties.length / PAGE_SIZE));
  const pageItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return properties.slice(start, start + PAGE_SIZE);
  }, [properties, page]);

  const liveCount = feedMeta?.counts?.borough ?? null;
  const curatedCount = feedMeta?.counts?.curated ?? null;
  const rentalCount = feedMeta?.counts?.rentals ?? null;
  const heading = listingType === "rent" ? "Homes for rent" : "Homes for sale";

  const gridBlock = (
    <>
      <div className={`grid gap-6 ${view === "split" ? "sm:grid-cols-1 xl:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-3"}`} data-testid="listings-grid">
        {pageItems.map((p, i) => (
          <motion.div key={p.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: Math.min(i * 0.05, 0.4) }}>
            <PropertyCard property={p} />
          </motion.div>
        ))}
      </div>
      {totalPages > 1 && (
        <div className="mt-10 flex items-center justify-center gap-4" data-testid="listings-pagination">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="flex items-center gap-1 px-3 py-2 rounded-sm border border-white/10 text-xs text-white/60 disabled:opacity-30 hover:border-brand hover:text-brand"
            data-testid="page-prev"
          >
            <CaretLeft size={14} /> Prev
          </button>
          <span className="text-sm text-white/50">Page {page} of {totalPages}</span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="flex items-center gap-1 px-3 py-2 rounded-sm border border-white/10 text-xs text-white/60 disabled:opacity-30 hover:border-brand hover:text-brand"
            data-testid="page-next"
          >
            Next <CaretRight size={14} />
          </button>
        </div>
      )}
    </>
  );

  const loadMoreBlock = properties.length >= fetchLimit && fetchLimit < 500 ? (
    <div className="mt-8 flex justify-center">
      <button
        type="button"
        data-testid="listings-load-more"
        onClick={() => setFetchLimit((n) => Math.min(500, n + FETCH_STEP))}
        className="px-5 py-2.5 rounded-sm border border-brand/40 text-brand text-xs uppercase tracking-wider hover:bg-brand/10"
      >
        Show more matches
      </button>
    </div>
  ) : null;

  return (
    <div className="bg-background min-h-screen">
      <SiteNav />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-28 pb-16">
        <div className="mb-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <p className="text-brand text-xs uppercase tracking-[0.3em] mb-3">Available Now</p>
            <h1 className="text-4xl md:text-5xl text-white">{heading}</h1>
            <p className="text-white/50 mt-2" data-testid="listings-count">
              {loading
                ? "Loading…"
                : `${properties.length} ${properties.length === 1 ? "home" : "homes"} matching your search`}
            </p>
            {(liveCount != null || curatedCount != null || rentalCount != null) && (
              <p className="text-white/35 text-xs mt-2" data-testid="listings-feed-meta">
                {curatedCount != null ? `${curatedCount} curated` : null}
                {curatedCount != null && liveCount != null ? " · " : null}
                {liveCount != null ? `${liveCount} live NYC sales` : null}
                {(liveCount != null || curatedCount != null) && rentalCount != null ? " · " : null}
                {rentalCount != null ? `${rentalCount} rentals` : null}
                {" · "}OneKey® MLS for the full feed
              </p>
            )}
          </div>

          <form
            className="flex w-full lg:max-w-md gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setQ(qDraft.trim());
            }}
          >
            <div className="relative flex-1">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/35" />
              <input
                data-testid="listings-search"
                value={qDraft}
                onChange={(e) => setQDraft(e.target.value)}
                placeholder="Neighborhood, train, walk-in…"
                className="w-full bg-[#101116] border border-white/10 rounded-sm pl-9 pr-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-brand focus:outline-none"
              />
            </div>
            <button
              type="submit"
              data-testid="listings-search-btn"
              className="px-4 py-2.5 rounded-sm bg-brand text-black text-xs uppercase tracking-wider font-medium hover:bg-brand-hover"
            >
              Search
            </button>
          </form>
        </div>

        {/* View toggle + sort + share */}
        <div className="mb-6 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setView("grid")}
            data-testid="view-grid"
            className={`flex items-center gap-2 rounded-sm border px-3.5 py-2 text-xs uppercase tracking-wider transition-colors ${view === "grid" ? "border-brand text-brand bg-brand/10" : "border-white/10 text-white/50 hover:text-white"}`}
          >
            <SquaresFour size={15} weight="duotone" /> Grid
          </button>
          <button
            type="button"
            onClick={() => setView("map")}
            data-testid="view-map"
            className={`flex items-center gap-2 rounded-sm border px-3.5 py-2 text-xs uppercase tracking-wider transition-colors ${view === "map" ? "border-brand text-brand bg-brand/10" : "border-white/10 text-white/50 hover:text-white"}`}
          >
            <MapTrifold size={15} weight="duotone" /> Map
          </button>
          <button
            type="button"
            onClick={() => setView("split")}
            data-testid="view-split"
            className={`flex items-center gap-2 rounded-sm border px-3.5 py-2 text-xs uppercase tracking-wider transition-colors ${view === "split" ? "border-brand text-brand bg-brand/10" : "border-white/10 text-white/50 hover:text-white"}`}
          >
            <Columns size={15} weight="duotone" /> <span className="hidden sm:inline">Map + list</span><span className="sm:hidden">Split</span>
          </button>
          <button
            type="button"
            onClick={shareSearch}
            data-testid="share-search"
            className="flex items-center gap-2 rounded-sm border border-white/10 px-3.5 py-2 text-xs uppercase tracking-wider text-white/50 hover:text-brand hover:border-brand/40"
          >
            <ShareNetwork size={15} weight="duotone" /> Share
          </button>
          <div className="w-full sm:w-auto sm:ml-auto flex flex-wrap items-center gap-2">
            <span className="text-[11px] uppercase tracking-wider text-white/35">Sort</span>
            {SORT_OPTIONS.map((s) => (
              <Chip key={s.value} active={sort === s.value} onClick={() => setSort(s.value)} testid={`sort-${s.value}`}>
                {s.label}
              </Chip>
            ))}
          </div>
        </div>

        <Link
          to="/mls-search"
          data-testid="listings-mls-banner"
          className="group flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 p-5 sm:p-6 rounded-sm border border-brand/30 bg-gradient-to-r from-brand/10 to-transparent hover:border-brand/60 transition-colors"
        >
          <div className="flex items-center gap-4">
            <span className="h-11 w-11 rounded-sm bg-brand/15 flex items-center justify-center shrink-0">
              <MagnifyingGlass size={20} weight="duotone" className="text-brand" />
            </span>
            <div>
              <p className="text-white text-base sm:text-lg">Looking for more? Search the entire live MLS</p>
              <p className="text-white/50 text-sm">Every active listing across New York via OneKey® MLS.</p>
            </div>
          </div>
          <span className="flex items-center gap-2 text-brand text-sm uppercase tracking-[0.12em] font-medium group-hover:gap-3 transition-all shrink-0">
            Search now <ArrowRight size={16} weight="bold" />
          </span>
        </Link>

        {/* Filters */}
        <div className="bg-[#101116] border border-white/5 rounded-sm mb-8">
          <div className="flex items-center gap-2 text-white/70 text-sm p-4 sm:p-5 border-b border-white/5">
            <SlidersHorizontal size={16} weight="duotone" className="text-brand" /> Filters
            <button
              type="button"
              className="md:hidden ml-1 flex items-center gap-1 text-xs text-white/40"
              onClick={() => setFiltersOpen((o) => !o)}
              data-testid="toggle-filters"
            >
              {filtersOpen ? "Hide" : "Show"}
              <CaretDown size={12} className={`transition-transform ${filtersOpen ? "rotate-180" : ""}`} />
            </button>
            <div className="ml-auto flex items-center gap-3 sm:gap-4">
              {hasFilters ? (
                <button type="button" onClick={clearAll} data-testid="clear-filters" className="flex items-center gap-1 text-xs text-white/40 hover:text-brand">
                  <X size={13} /> Clear
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => setAlertOpen(true)}
                data-testid="get-alerts-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-brand/40 text-xs text-brand hover:bg-brand/10 transition-colors"
              >
                <BellRinging size={14} weight="duotone" /> <span className="hidden xs:inline sm:inline">Alerts</span>
              </button>
            </div>
          </div>

          <div className={`${filtersOpen ? "block" : "hidden md:block"} p-4 sm:p-6 divide-y divide-white/5`}>
            {/* Row 1 — the two most important choices: sale/rent + budget */}
            <div className="pb-5 grid gap-5 md:grid-cols-2">
              <FilterSection label="I'm looking to" hint="Sale or rent — chips below adjust automatically.">
                <div className="flex flex-wrap gap-2" data-testid="listing-type-tabs">
                  {LISTING_TYPES.map((s) => (
                    <Chip
                      key={s.value}
                      active={listingType === s.value}
                      onClick={() => { setListingType(s.value); setPriceIdx(0); setTypes([]); }}
                      testid={`filter-listing-type-${s.value}`}
                    >
                      {s.label}
                    </Chip>
                  ))}
                </div>
              </FilterSection>
              <FilterSection label={listingType === "rent" ? "Monthly rent" : "Sale price"}>
                <div className="flex flex-wrap gap-2">
                  {priceOptions.map((p, i) => (
                    <Chip key={p.label} active={priceIdx === i} onClick={() => setPriceIdx(i)} testid={`filter-price-${i}`}>{p.label}</Chip>
                  ))}
                </div>
              </FilterSection>
            </div>

            {/* Row 2 — home shape */}
            <div className="py-5 grid gap-5 md:grid-cols-3">
              <FilterSection label="Home type" hint={listingType === "rent" ? "NYC rentals lean apartment / multi-family." : null}>
                <div className="flex flex-wrap gap-2">
                  {homeTypes.map((t) => (
                    <Chip key={t} active={types.includes(t)} onClick={() => toggle(types, setTypes, t)} testid={`filter-ptype-${slug(t)}`}>{t}</Chip>
                  ))}
                </div>
              </FilterSection>
              <FilterSection label="Bedrooms">
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2">
                    <span className="text-[10px] uppercase tracking-wider text-white/30 self-center pr-1">Min</span>
                    {MIN_BED_CHIPS.map((b) => (
                      <Chip key={`min-${b.value || "any"}`} active={minBeds === b.value} onClick={() => setMinBeds(b.value)} testid={`filter-beds-${b.value || "any"}`}>{b.label}</Chip>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="text-[10px] uppercase tracking-wider text-white/30 self-center pr-1">Max</span>
                    {MAX_BED_CHIPS.map((b) => (
                      <Chip key={`max-${b.value || "any"}`} active={maxBeds === b.value} onClick={() => setMaxBeds(b.value)} testid={`filter-max-beds-${b.value || "any"}`}>{b.label}</Chip>
                    ))}
                  </div>
                </div>
              </FilterSection>
              <FilterSection label="Min baths">
                <div className="flex flex-wrap gap-2">
                  {["", "1", "1.5", "2", "3"].map((b) => (
                    <Chip key={`bath-${b || "any"}`} active={minBaths === b} onClick={() => setMinBaths(b)} testid={`filter-baths-${b || "any"}`}>{b ? `${b}+` : "Any"}</Chip>
                  ))}
                </div>
              </FilterSection>
            </div>

            {/* Row 3 — where + must-haves */}
            <div className="py-5 space-y-5">
              <FilterSection label="Area focus" hint="Bronx/NYC from live feed · Westchester is curated until full MLS IDX.">
                <div className="flex flex-wrap gap-2 mb-2">
                  {AREA_GROUPS.map((g) => (
                    <Chip
                      key={g.value}
                      active={areaGroups.includes(g.value)}
                      onClick={() => toggle(areaGroups, setAreaGroups, g.value)}
                      testid={`filter-area-group-${slug(g.value)}`}
                    >
                      {g.label}
                    </Chip>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2">
                  {REGIONS.map((r) => (
                    <Chip key={r} active={regions.includes(r)} onClick={() => toggle(regions, setRegions, r)} testid={`filter-region-${slug(r)}`}>{r}</Chip>
                  ))}
                </div>
              </FilterSection>
              <FilterSection label="Must-haves" hint="Lifestyle chips — transit, pets, no-fee rentals, etc.">
                <div className="flex flex-wrap gap-2">
                  {LIFESTYLE_FILTERS.filter((f) => !f.rentOnly || listingType === "rent").map((f) => {
                    const active = f.param === "near_transit" ? nearTransit
                      : f.param === "walk_in" ? walkIn
                        : amenities.includes(f.id);
                    const onClick = () => {
                      if (f.param === "near_transit") setNearTransit((v) => !v);
                      else if (f.param === "walk_in") setWalkIn((v) => !v);
                      else toggle(amenities, setAmenities, f.id);
                    };
                    return (
                      <Chip key={f.id} active={active} onClick={onClick} testid={`filter-lifestyle-${f.id}`}>
                        {f.label}
                      </Chip>
                    );
                  })}
                </div>
              </FilterSection>
            </div>

            {/* Row 4 — neighborhood context + inventory (advanced) */}
            <div className="pt-5 grid gap-5 md:grid-cols-3">
              <FilterSection label="Flood risk" hint="NYC hurricane evacuation data. Non-NYC listings may show N/A.">
                <div className="flex flex-wrap gap-2" data-testid="flood-risk-chips">
                  {FLOOD_RISK_FILTERS.map((f) => (
                    <Chip key={f.value} active={floodRisk === f.value} onClick={() => setFloodRisk(f.value)} testid={`filter-flood-${f.value}`}>
                      {f.label}
                    </Chip>
                  ))}
                </div>
              </FilterSection>
              <FilterSection label="School district" hint="NYC community school districts only.">
                <select
                  value={schoolDistrict}
                  onChange={(e) => setSchoolDistrict(e.target.value)}
                  data-testid="filter-school-district"
                  className="w-full bg-[#0d0e13] border border-white/10 rounded-sm px-3 py-2.5 text-sm text-white focus:border-brand focus:outline-none"
                >
                  <option value="">Any district</option>
                  {SCHOOL_DISTRICTS.map((district) => (
                    <option key={district} value={district}>{district}</option>
                  ))}
                </select>
              </FilterSection>
              <FilterSection label="Inventory source">
                <div className="flex flex-wrap gap-2">
                  {[
                    { v: "all", l: "All homes" },
                    { v: "borough", l: "Live NYC market" },
                    { v: "curated", l: "Curated picks" },
                  ].map((s) => (
                    <Chip key={s.v} active={source === s.v} onClick={() => setSource(s.v)} testid={`filter-source-${s.v}`}>
                      {s.l}
                    </Chip>
                  ))}
                </div>
              </FilterSection>
            </div>
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="py-24 flex justify-center"><div className="h-8 w-8 rounded-full border-2 border-brand border-t-transparent animate-spin" /></div>
        ) : loadError ? (
          <div className="py-24 text-center" data-testid="listings-error">
            <p className="text-white/50 mb-4">{loadError}</p>
            <button type="button" onClick={load} className="text-brand text-sm hover:text-brand-hover">Try again</button>
          </div>
        ) : properties.length === 0 ? (
          <div className="py-24 text-center" data-testid="listings-empty">
            <p className="text-white/50 mb-4">No homes match your filters right now.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <button type="button" onClick={clearAll} className="text-brand text-sm hover:text-brand-hover">Clear filters</button>
              <Link to="/mls-search" className="text-white/50 text-sm hover:text-brand">Search the full MLS →</Link>
            </div>
          </div>
        ) : view === "map" ? (
          <>
            <ListingsMap properties={properties} heightClass="h-[55vh] md:h-[70vh]" />
            {loadMoreBlock}
          </>
        ) : view === "split" ? (
          <>
            <div className="grid lg:grid-cols-2 gap-6 items-start" data-testid="listings-split">
              <div className="order-2 lg:order-1">{gridBlock}</div>
              <div className="order-1 lg:order-2 lg:sticky lg:top-24">
                <ListingsMap
                  properties={properties}
                  heightClass="h-[40vh] lg:h-[min(70vh,720px)]"
                  compact
                />
                <p className="text-white/30 text-[11px] mt-2">Map updates with your filters — tap a pin for details.</p>
              </div>
            </div>
            {loadMoreBlock}
          </>
        ) : (
          <>
            {gridBlock}
            {loadMoreBlock}
          </>
        )}

        {recentSales.length > 0 && listingType !== "rent" && (
          <section className="mt-20" data-testid="recent-sales">
            <div className="flex items-end justify-between mb-6 gap-4">
              <div>
                <p className="text-brand text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2">
                  <ChartLineUp size={14} weight="duotone" /> Market pulse
                </p>
                <h2 className="text-2xl md:text-3xl text-white">Recently sold in NYC</h2>
                <p className="text-white/40 text-sm mt-1">Official NYC Open Data rolling sales — comps, not active listings.</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {recentSales.map((s) => (
                <div
                  key={s.id}
                  className="bg-[#101116] border border-white/5 rounded-sm p-4 hover:border-white/15 transition-colors"
                  data-testid={`recent-sale-${s.id}`}
                >
                  <p className="text-brand font-serif text-xl mb-1">{formatPrice(s.price)}</p>
                  <p className="text-white text-sm line-clamp-2 mb-2">{s.address}</p>
                  <p className="text-white/35 text-xs uppercase tracking-wider">
                    {s.property_type} · {s.region}
                    {s.sale_date ? ` · ${s.sale_date}` : ""}
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        <p className="text-white/25 text-[11px] mt-12 max-w-3xl">
          Filters cover lifestyle details (transit, walk-in, pets, no-fee rentals, parking) plus beds/price/type.
          Live NYC inventory refreshes from Borough; share your search link for the same results. Full MLS via OneKey®.
        </p>
      </div>

      <Dialog open={alertOpen} onOpenChange={setAlertOpen}>
        <DialogContent className="bg-[#101116] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-2">
              <BellRinging size={22} weight="duotone" className="text-brand" /> Get new-listing alerts
            </DialogTitle>
            <DialogDescription className="text-white/50">
              We&apos;ll email you when a new home matches your current filters.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="lux-label">Full name</label>
              <input data-testid="alert-name" className="lux-input" value={alertForm.name} onChange={(e) => setAlertForm({ ...alertForm, name: e.target.value })} placeholder="Jane Doe" />
            </div>
            <div>
              <label className="lux-label">Email</label>
              <input data-testid="alert-email" type="email" className="lux-input" value={alertForm.email} onChange={(e) => setAlertForm({ ...alertForm, email: e.target.value })} placeholder="you@email.com" />
            </div>
            <div>
              <label className="lux-label">Phone (optional)</label>
              <input data-testid="alert-phone" type="tel" className="lux-input" value={alertForm.phone} onChange={(e) => setAlertForm({ ...alertForm, phone: e.target.value })} placeholder="+1 (555) 123-4567" />
            </div>
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setAlertOpen(false)} className="px-5 py-2.5 rounded-sm border border-white/15 text-sm text-white/70 hover:text-white">Cancel</button>
            <button type="button" onClick={saveAlert} disabled={savingAlert} data-testid="alert-save" className="px-6 py-2.5 rounded-sm bg-brand text-black text-sm uppercase tracking-wider font-medium hover:bg-brand-hover disabled:opacity-60">
              {savingAlert ? "Saving…" : "Notify me"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
